import { useCallback, useEffect, useMemo, type JSX } from 'react';

import type { FabricChannel } from '@atlaskit/analytics-listeners';
import {
  Placement as BasePlacement,
  type PlacementProps as BasePlacementProps,
} from '@atlassian/post-office-placement-remote';
import { Analytics } from '@trello/atlassian-analytics';
import { ErrorBoundary } from '@trello/error-boundaries';
import { initializeUFOPublisher } from '@trello/ufo/react';

import { ConditionalFabricAnalyticsListeners } from './ConditionalFabricAnalyticsListeners';
import { PostOfficeProvider } from './PostOfficeProvider';

export type OnEvent = NonNullable<BasePlacementProps['onEvent']>;

const Null = (): JSX.Element | null => null;

const VITAL_STATS_CONFIG = {
  taskName: 'load-postOfficePlacement',
  source: '@atlassian/post-office-placement-remote',
} as const;

/**
 * This is a version of the Placement component that doesn't need to be manually wrapped in a PostOfficeContextProvider
 * @param props The props for the Placement component, product and locale will be automatically set to 'trello' and the current locale respectively.
 *
 * Set the shouldIgnoreFabricAnalyticsListeners prop to true if the Placement component is already wrapped within a FabricAnalyticsListeners component.
 * This avoids firing identical analytics events twice.
 * */

export const Placement = <TChildren = BasePlacementProps['children'],>({
  onEvent: externalOnEvent,
  shouldIgnoreFabricAnalyticsListeners = false,
  excludedFabricAnalyticsChannels,
  ...props
}: Omit<BasePlacementProps, 'children'> &
  TChildren & {
    shouldIgnoreFabricAnalyticsListeners?: boolean;
    excludedFabricAnalyticsChannels?: FabricChannel[];
  }): JSX.Element => {
  useEffect(initializeUFOPublisher, []);

  const vitalStatsTracker = useMemo(() => {
    const VITAL_STATS_CONFIG_WITH_PLACEMENT = {
      ...VITAL_STATS_CONFIG,
      attributes: {
        placementId: props.placementId,
      },
    };
    const traceId = Analytics.startTask(VITAL_STATS_CONFIG_WITH_PLACEMENT);
    return {
      success: (): void =>
        Analytics.taskSucceeded({
          ...VITAL_STATS_CONFIG_WITH_PLACEMENT,
          traceId,
        }),
      fail: (error: unknown): void => {
        Analytics.taskFailed({
          ...VITAL_STATS_CONFIG_WITH_PLACEMENT,
          traceId,
          error,
        });
      },
      abort: (): void => {
        Analytics.taskAborted({
          ...VITAL_STATS_CONFIG_WITH_PLACEMENT,
          traceId,
        });
      },
    };
  }, [props.placementId]);

  const onEvent = useCallback<OnEvent>(
    ({ type, error }) => {
      switch (type) {
        case 'unmount':
          vitalStatsTracker.abort();
          break;
        case 'component-ready':
          vitalStatsTracker.success();
          break;
        case 'render-error':
        case 'fetch-error':
        case 'component-error':
          vitalStatsTracker.fail(error);
          break;
        default:
          break;
      }
      externalOnEvent?.({ type, error });
    },
    [externalOnEvent, vitalStatsTracker],
  );

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-electric',
        feature: 'Post Office Placement',
      }}
      errorHandlerComponent={Null}
    >
      <ConditionalFabricAnalyticsListeners
        shouldIgnoreFabricAnalyticsListeners={
          shouldIgnoreFabricAnalyticsListeners
        }
        excludedChannels={excludedFabricAnalyticsChannels}
      >
        <PostOfficeProvider>
          <BasePlacement {...props} onEvent={onEvent} />
        </PostOfficeProvider>
      </ConditionalFabricAnalyticsListeners>
    </ErrorBoundary>
  );
};
